import React, { useState, useEffect } from 'react';
import { LeftBar, Header } from 'components';
import { MobHeader } from '../../components/MobHeader';
import { useWeb3React } from '@web3-react/core';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { GET_USER_DATA } from '../../helpers/graphRequests';
import { updateUser } from '../../store/userSlice';
import { getUser } from '../../store/userSlice/selectors';
import { useQuery } from '@apollo/client';
import { UserRepository } from '../../connectors/repositories/user';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import LoadingLogo from '../../assets/lottieAnimations/loadingLogo.json';
import { Footer } from '../../components/Footer';
import { useTranslation } from 'react-i18next';
import { usePreviewMode } from '../../helpers/hooks/useCheckPreviewMode';
import { normalizeAvatar } from '../../helpers/avatar';

export const BaseLayout = ({ children, className, childrenClassName, withShadow }) => {
  const { t } = useTranslation();
  const { account } = useWeb3React();
  const { previewUserAddress } = usePreviewMode();
  const [userAddress, setUserAddress] = useState(null);
  const [isCalledUserNick, setIsCalledUserNick] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [nickname, setNickname] = useState(null);

  useEffect(() => {
    setAvatar(normalizeAvatar(localStorage.getItem('avatar')));
    setNickname(localStorage.getItem('nickname'));
  }, []);

  useEffect(() => {
    if (previewUserAddress) {
      setUserAddress(previewUserAddress);
    } else {
      setUserAddress(account);
    }
  }, [account, previewUserAddress]);

  const dispatch = useDispatch();
  const currentUser = useSelector(getUser);
  const [isNeedToCall, setIsNeedToCall] = useState(false);
  const navigate = useNavigate();

  const setUserInfo = async () => {
    setIsCalledUserNick(false);

    try {
      const result = await UserRepository.getProfileWithoutToken(userAddress);
      if (result) {
        setAvatar(normalizeAvatar(result?.imageUrl));
        setNickname(result?.nickname);
      } else {
        setAvatar(null);
        setNickname(null);
      }
    } catch (e) {
      setAvatar(null);
      setNickname(null);
    } finally {
      setIsCalledUserNick(true);
    }
  };

  // Загружаем аватар и никнейм асинхронно, не блокируя основной рендеринг
  useEffect(() => {
    if (userAddress) {
      // Сбрасываем аватар и никнейм при переключении пользователя
      setAvatar(null);
      setNickname(null);
      setIsCalledUserNick(false);

      // Обновляем Redux store, чтобы убрать старые данные
      if (currentUser?.id) {
        dispatch(
          updateUser({
            ...currentUser,
            avatar: null,
            nickname: null,
            rank:
              currentUser?.totalPartners * 1000 +
              (currentUser?.totalTeam / 10) * 1500 +
              levelsPoints(currentUser?.levelsX2, 2) +
              levelsPoints(currentUser?.levelsX4, 4) +
              0, // userDataPoints = 0, так как аватар и никнейм сброшены
          }),
        );
      }

      // Вызываем асинхронно без await, чтобы не блокировать
      setUserInfo();
    }
  }, [userAddress]);

  const { data, error, loading, called } = useQuery(GET_USER_DATA, {
    variables: { user: userAddress },
    skip: !isNeedToCall,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!userAddress) return undefined;

    setUserAddress(userAddress?.toLocaleLowerCase());
    setIsNeedToCall(true);

    const pollUser = () => {
      if (!document.hidden) {
        setIsNeedToCall(true);
      }
    };

    const interval = setInterval(pollUser, 300000);
    document.addEventListener('visibilitychange', pollUser);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', pollUser);
    };
  }, [userAddress]);

  const levelsPoints = (levels = [], k = 2) => {
    const activatePoints = levels?.reduce((acc, item) => {
      if (!!item.isActivated) {
        return (acc = acc + 1000);
      }
      return acc;
    }, 0);

    const restartPoints = levels?.reduce((acc, item) => {
      if (!!item.isActivated) {
        return (acc = acc + item?.recyclesTotal * k * 100);
      }
      return acc;
    }, 0);

    return activatePoints + restartPoints;
  };

  const userDataPoints = avatar || nickname ? 3000 : 0;

  // Основной useEffect - обновляет пользователя сразу, не ждет аватар/никнейм
  useEffect(() => {
    if (data && !loading) {
      if (Object.keys(data)?.length && data?.user === null && !loading) {
        navigate('/');
      }
      if (!!data?.user?.id) {
        const {
          id,
          refNumber,
          referral,
          internalId,
          totalPartners,
          partners24Timestamp,
          totalTeam,
          team24Timestamp,
          levelsX2,
          levelsX4,
          totalRewardX2,
          totalRewardX4,
          registrationTime,
        } = data?.user;

        const calculateRank =
          data?.user?.totalPartners * 1000 +
          (data?.user?.totalTeam / 10) * 1500 +
          levelsPoints(data?.user?.levelsX2, 2) +
          levelsPoints(data?.user?.levelsX4, 4) +
          userDataPoints;

        dispatch(
          updateUser({
            id,
            rank: calculateRank,
            internalId,
            refNumber,
            referral,
            totalPartners,
            partners24Timestamp,
            totalTeam,
            team24Timestamp,
            totalRewardX2,
            totalRewardX4,
            registrationTime,
            levelsX2,
            levelsX4,
            avatar: avatar,
            nickname: nickname,
          }),
        );
        setIsNeedToCall(false);
      } else {
        dispatch(updateUser({ id: '' }));
      }
    }
  }, [data, loading]);

  // Отдельный useEffect для обновления аватара и никнейма в Redux после их загрузки
  useEffect(() => {
    if (currentUser?.id && isCalledUserNick) {
      const newUserDataPoints = avatar || nickname ? 3000 : 0;
      const currentRank =
        currentUser?.totalPartners * 1000 +
        (currentUser?.totalTeam / 10) * 1500 +
        levelsPoints(currentUser?.levelsX2, 2) +
        levelsPoints(currentUser?.levelsX4, 4) +
        newUserDataPoints;

      // Обновляем только если аватар или никнейм изменились
      if (currentUser?.avatar !== avatar || currentUser?.nickname !== nickname || currentUser?.rank !== currentRank) {
        dispatch(
          updateUser({
            ...currentUser,
            avatar: avatar,
            nickname: nickname,
            rank: currentRank,
          }),
        );
      }
    }
  }, [avatar, nickname, isCalledUserNick, currentUser?.id]);

  if ((loading && !currentUser?.id) || (data?.user === null && !loading)) {
    return (
      <div className="z-[10] relative flex items-center justify-center w-screen h-screen">
        <Lottie
          className="z-[3] h-[12rem] w-[12rem] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          animationData={LoadingLogo}
          loop={true}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative flex flex-col bg-[#0C0C0E] items-center justify-center relative min-h-screen lg:bg-[#020204] ${className} `}
    >
      <MobHeader />
      <div className="relative flex w-full z-[1] max-w-[137rem] relative">
        <LeftBar />
        <div className="flex flex-col w-full lg:pt-[6.4rem] lg:pl-0 ">
          <Header />
          <div className="flex flex-col justify-between w-full h-full">
            <div
              className={`flex justify-between flex-col space-y-[2rem] w-full h-full p-[2rem] lg:p-0 ${
                childrenClassName ? childrenClassName : ''
              }`}
            >
              {children}
            </div>
            <div className="p-[2rem] lg:p-0 text-left w-full space-x-[0.4rem] text-[1.4rem] leading-[1.96rem] max-w-[137rem] lg:hidden ">
              <span className="text-white-500">{t('footer.copyright')}</span>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <img
        className="absolute left-0 max-h-full h-full lg:hidden"
        src="/images/layouts/base/shadows/leftShadow.webp"
        alt=""
      />
    </div>
  );
};
